import { DateTime } from "luxon";
import { SchoolHoursModel } from "model";

import { roundUpToNearest15 } from ".";
import { getDefaultEndTime, isSameDateTime, type TimesTableRowType } from "./useRequestDate";

/**
 * Creates a new end date time.
 *
 * Should only be used when generating a new end time.
 *
 * @example
 * // Case #1: Start Time + 1 hour <= School End Time
 * // Returns: Start Time + 1 hour
 *
 * // Case #2: School End Time < Start Time + 1 hour <= End of Day
 * // Returns Start Time + 1 hour
 *
 * // Case #3: School End Time < End of Day < Start Time + 1 hour
 * // Returns End of Day
 */
export function newEndDateTime(params: {
  startDateTime: DateTime;
  schoolHours?: SchoolHoursModel;
}) {
  const { startDateTime, schoolHours } = params;

  // The minimum end date time must be at least 1 hour from the start date time
  const minEndDateTime = startDateTime.plus({ hour: 1 });

  // If the minimum end date is before the default school end time
  if (minEndDateTime <= getDefaultEndTime(startDateTime, schoolHours)) {
    // Then return the default school end time
    return getDefaultEndTime(startDateTime, schoolHours);
  }

  // Otherwise, this means that the minimum end date time is after the default
  // school end time. Return the minimum between the minEndDateTime and the end
  // of the day as we don't want to roll over to the next day.
  return DateTime.min(minEndDateTime, startDateTime.endOf("day"));
}

/**
 * Ensures that the Start and End time of each Interval is valid. The following
 * checks are performed:
 *
 * 1. If the start time is less then 1h(~15m) from the current time
 * 1.1 Then update the start time to be 1h(~15m) from the current time
 *
 * 2. If the end time is less than 2h(~15m) from the current time
 * 2.1 Then update the end time to be 2h(~15m) from the current time
 *
 * 3. If the start time was changed, and the end time is less than 1 hour from
 * the start time
 * 3.1 Then update the end time to be 1 hour from the start time.
 *
 * 4. If the end time was changed, and the start time is less than 1 hour from
 * the end time
 * 4.1 Then update the start time to be 1 hour from the end time.
 */
export function fixStartAndEndTimes(params: {
  newIntervals: Array<TimesTableRowType>;
  oldIntervals: Array<TimesTableRowType>;
  /** The current time in the School's timezone. */
  now: DateTime;
}) {
  const { newIntervals, oldIntervals, now } = params;

  // Groups both intervals by day to allow easy comparison
  const groupNewIntervalsByDay = newIntervals.reduce(
    (acc, interval) => {
      const day = interval.startTime.toISODate();
      acc[day] = interval;
      return acc;
    },
    {} as Record<string, TimesTableRowType>,
  );
  const groupOldIntervalsByDay = oldIntervals.reduce(
    (acc, interval) => {
      const day = interval.startTime.toISODate();
      acc[day] = interval;
      return acc;
    },
    {} as Record<string, TimesTableRowType>,
  );

  // If there is an interval today
  const currentDay = now.toISODate();
  if (groupNewIntervalsByDay[currentDay]) {
    // Determine the minimum start time for today
    const minStartTime = roundUpToNearest15(now.plus({ hour: 1 }));
    // And the minimum end time for today
    const minEndTime = minStartTime.plus({ hours: 1 });

    // If the start time is less than the minimum start time, then update the
    // start time to be the minimum start time
    if (groupNewIntervalsByDay[currentDay].startTime < minStartTime) {
      groupNewIntervalsByDay[currentDay].startTime = minStartTime;
    }

    // If the end time is less than the minimum end time, then update the end
    // time to be the minimum end time
    if (groupNewIntervalsByDay[currentDay].endTime < minEndTime) {
      groupNewIntervalsByDay[currentDay].endTime = minEndTime;
    }
  }

  // For each newInterval which matches an oldInterval
  for (const [day, newInterval] of Object.entries(groupNewIntervalsByDay)) {
    const oldInterval = groupOldIntervalsByDay[day];

    // If there is not match, move to the next day
    if (!oldInterval) continue;

    // Otherwise, check if the start and end time are at least 1 hour apart
    const startAndEndTimeMinuteApart = newInterval.endTime.diff(
      newInterval.startTime,
      "minutes",
    ).minutes;

    // If they are equal or more than 1 hour apart, move to the next day
    if (startAndEndTimeMinuteApart >= 60) continue;

    // Otherwise
    // If the start time was changed
    if (!isSameDateTime(newInterval.startTime, oldInterval.startTime)) {
      // Then update the end time to be 1 hour from the start time of the end
      // of the day, whichever is earlier.
      newInterval.endTime = DateTime.min(
        newInterval.startTime.plus({ hours: 1 }),
        newInterval.startTime.endOf("day"),
      );
    }
    // Otherwise, the end time was changed
    else if (!isSameDateTime(newInterval.endTime, oldInterval.endTime)) {
      // Then update the start time to be 1 hour from the end time or the start
      // of the day, whichever is latest.
      newInterval.startTime = DateTime.max(
        newInterval.endTime.minus({ hours: 1 }),
        newInterval.endTime.startOf("day"),
      );
    }
  }

  return newIntervals;
}
